import type { flows } from '@adc/blueprint'
import { track } from './eventTracking'
import eventNames from './eventNames'

/**
 * Creates an EventSink that routes useFlow lifecycle events through
 * the standard @adc/analytics track() pipeline (GTM + Amplitude).
 *
 * @param flowName - Stable identifier for the flow (e.g. 'registration', 'guided_offer')
 * @param extraProps - Additional properties to attach to every event (e.g. listing context)
 *
 * @example
 * ```tsx
 * import { createFlowEmitter } from '@adc/analytics'
 * import { flows } from '@adc/blueprint'
 *
 * const { data, actions } = flows.hooks.useFlow({
 *   definition: mySteps,
 *   context,
 *   emitter: createFlowEmitter('registration', { morpheusListingId: listing.listing_id }),
 * })
 * ```
 */
export function createFlowEmitter<S extends string>(
  flowName: string,
  extraProps: Record<string, any> = {}
): flows.EventSink<S> {
  return {
    onStep(step, payload) {
      track(eventNames.flowStepViewed, {
        flow_name: flowName,
        step_id: step,
        from_step: payload?.from ?? null,
        ...extraProps,
      })
    },

    on(event, payload) {
      track(event, {
        flow_name: flowName,
        from_step: payload?.from ?? null,
        to_step: payload?.to ?? null,
        ...extraProps,
      })
    },
  }
}

/**
 * Composes multiple EventSink emitters into a single emitter that
 * dispatches to all of them. Use when a flow needs both app-specific
 * tracking (e.g. Datadog domain events) and generic Amplitude tracking.
 *
 * @example
 * ```tsx
 * import { createFlowEmitter, composeEmitters } from '@adc/analytics'
 *
 * const emitter = composeEmitters(
 *   createRegistrationEmitter(context, baseTrackingKey, featureFlags), // Datadog + domain events
 *   createFlowEmitter('registration', { morpheusListingId }),          // Amplitude
 * )
 *
 * const { data, actions } = flows.hooks.useFlow({
 *   definition: steps,
 *   context,
 *   emitter,
 * })
 * ```
 */
export function composeEmitters<S extends string>(
  ...emitters: (flows.EventSink<S> | undefined)[]
): flows.EventSink<S> {
  return {
    onStep(step, payload) {
      emitters.forEach(e => e?.onStep?.(step, payload))
    },
    on(event, payload) {
      emitters.forEach(e => e?.on?.(event, payload))
    },
  }
}
